<template>
  <div class="flex flex-col h-full">

    <!-- Loading mailbox -->
    <div v-if="loadingMailbox" class="text-sm text-gray-500 py-4 text-center">
      <font-awesome-icon :icon="['fas', 'circle-notch']" spin class="mr-2" />
      Loading…
    </div>

    <!-- Not configured -->
    <div v-else-if="!mailbox || mailbox.status === 'inactive'" class="flex items-center justify-center py-12">
      <div class="max-w-xs text-center">
        <font-awesome-icon :icon="['fas', 'envelope']" class="text-gray-300 text-3xl mb-3" />
        <p class="text-sm font-medium text-gray-700 mb-1">Provider Messaging not configured</p>
        <p class="text-xs text-gray-500">
          Your account has not been set up for provider messaging.
          Contact your administrator if you think this is a mistake.
        </p>
      </div>
    </div>

    <template v-else>

      <!-- Header -->
      <div class="flex items-center justify-between mb-4 shrink-0">
        <h3 class="text-base font-semibold text-gray-900">Provider Messages</h3>
        <button class="btn-primary text-sm" @click="openNewCompose">
          <font-awesome-icon :icon="['fas', 'pen-to-square']" class="mr-1" />
          New Message
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-sm text-gray-500 py-4 text-center">
        <font-awesome-icon :icon="['fas', 'circle-notch']" spin class="mr-2" />
        Loading messages…
      </div>

      <!-- Empty -->
      <div v-else-if="!threads.length" class="text-sm text-gray-500 py-4 text-center">
        No messages linked to this patient yet.
      </div>

      <!-- Thread list -->
      <div v-else class="flex-1 overflow-y-auto flex flex-col gap-3">
        <div
          v-for="thread in threads"
          :key="thread.normalizedKey"
          class="border border-gray-200 rounded-lg overflow-hidden"
        >
          <!-- Thread header -->
          <button
            type="button"
            class="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
            @click="toggleExpand(thread.normalizedKey)"
          >
            <div class="min-w-0">
              <p class="font-semibold text-sm text-gray-900 truncate">{{ normalizeSubject(thread.subject) || '(No subject)' }}</p>
              <p class="text-xs text-gray-500 mt-0.5">{{ thread.messages.length }} message{{ thread.messages.length !== 1 ? 's' : '' }} · {{ formatDate(thread.latestAt) }}</p>
            </div>
            <font-awesome-icon
              :icon="['fas', expandedKey === thread.normalizedKey ? 'chevron-up' : 'chevron-down']"
              class="text-gray-400 text-xs ml-3 shrink-0"
            />
          </button>

          <!-- Conversation bubbles -->
          <div v-if="expandedKey === thread.normalizedKey" class="px-4 py-3 flex flex-col gap-3 bg-white">
            <router-link
              v-for="msg in thread.messages"
              :key="msg.id"
              :to="`/caremanagement/provider-messaging?messageId=${msg.external_id}`"
              :class="['flex', msg.direction === 'outbound' ? 'justify-end' : 'justify-start']"
            >
              <div :class="['max-w-[80%] flex flex-col gap-0.5', msg.direction === 'outbound' ? 'items-end' : 'items-start']">
                <p class="text-xs text-gray-500 px-1 truncate">
                  {{ msg.direction === 'inbound' ? `From: ${participantLabel(msg, 'from')}` : `To: ${participantLabel(msg, 'to')}` }}
                </p>
                <div
                  :class="[
                    'px-3 py-2.5 rounded-2xl text-sm leading-snug hover:opacity-90 transition-opacity cursor-pointer',
                    msg.direction === 'outbound'
                      ? 'bg-blue-600 text-white rounded-br-sm'
                      : 'bg-gray-100 text-gray-900 rounded-bl-sm',
                  ]"
                >
                  <p v-if="messageDetails[msg.id]?.loading" class="italic opacity-75">Loading…</p>
                  <p v-else-if="messageBodyText(msg)" class="whitespace-pre-wrap">{{ messageBodyText(msg) }}</p>
                  <p v-else class="italic opacity-75">(No preview available)</p>
                  <p
                    :class="['flex items-center gap-1.5 text-xs mt-1', msg.direction === 'outbound' ? 'text-blue-200' : 'text-gray-500']"
                  >
                    <span>{{ formatDateTime(msg.sent_at || msg.received_at || msg.created_at) }}</span>
                    <span v-if="msg.has_attachments" class="flex items-center gap-0.5">
                      ·
                      <font-awesome-icon :icon="['fas', 'paperclip']" class="text-[10px]" />
                      {{ msg.attachment_count }}
                    </span>
                  </p>
                </div>
              </div>
            </router-link>

            <div class="flex justify-end">
              <button type="button" class="btn-secondary text-xs" @click="openReplyCompose(thread)">
                <font-awesome-icon :icon="['fas', 'reply']" class="mr-1" />
                Reply
              </button>
            </div>
          </div>
        </div>
      </div>

    </template>

    <!-- Compose modal -->
    <ComposeMessageModal
      v-model="showCompose"
      :mode="composeMode"
      :locked-patient="lockedPatientObj"
      :initial-subject="composeSubject"
      :initial-to-email="composeToEmail"
      :initial-to-display-name="composeToDisplayName"
      @sent="onComposeSent"
    />

  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ComposeMessageModal from '@/components/shared/ComposeMessageModal.vue';
import * as messagingApi from '@/services/api/providerMessaging.js';
import { participantDisplayName, loadAddressBook, addressBookDisplayName } from '@/utils/providerMessaging.js';

function normalizeSubject(subject) {
  return (subject || '').replace(/^(re|fwd?|fw):\s*/i, '').trim().toLowerCase()
}

function messageTimestamp(msg) {
  return msg.sent_at || msg.received_at || msg.created_at
}

function groupByThread(messages) {
  const threads = new Map()
  for (const msg of messages) {
    const key = normalizeSubject(msg.subject)
    if (!threads.has(key)) threads.set(key, [])
    threads.get(key).push(msg)
  }
  for (const msgs of threads.values()) {
    msgs.sort((a, b) => new Date(messageTimestamp(a)) - new Date(messageTimestamp(b)))
  }
  return [...threads.entries()]
    .map(([key, msgs]) => ({
      subject: msgs[0].subject,
      normalizedKey: key,
      messages: msgs,
      latestAt: messageTimestamp(msgs[msgs.length - 1]),
    }))
    .sort((a, b) => new Date(b.latestAt) - new Date(a.latestAt))
}

export default {
  name: 'Provider_Messages',
  components: { FontAwesomeIcon, ComposeMessageModal },

  props: {
    patientId: {
      type: Number,
      required: true,
    },
    patientIdentifier: {
      type: String,
      required: true,
    },
    patientName: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      loadingMailbox: false,
      mailbox: null,
      threads: [],
      loading: false,
      expandedKey: null,
      messageDetails: {}, // msg.id -> { loading, detail }
      addressBook: null,

      // compose state
      showCompose: false,
      composeMode: 'new',
      composeSubject: '',
      composeToEmail: '',
      composeToDisplayName: '',
    };
  },

  computed: {
    lockedPatientObj() {
      return { id: this.patientId, name: this.patientName || this.patientIdentifier };
    },
  },

  async mounted() {
    this.loadingMailbox = true;
    try {
      this.mailbox = await messagingApi.getMailbox();
      if (this.mailbox.status === 'active') {
        await this.loadMessages();
      }
    } catch (e) {
      if (e?.response?.status !== 404) {
        console.error('[Provider_Messages] failed to load mailbox:', messagingApi.errorMessage(e));
      }
    } finally {
      this.loadingMailbox = false;
    }
    loadAddressBook().then((book) => { this.addressBook = book; });
  },

  methods: {
    async loadMessages() {
      this.loading = true;
      try {
        const messages = await messagingApi.listEntityMessages(this.patientId);
        this.threads = groupByThread(messages);
      } catch (e) {
        console.error('[Provider_Messages] failed to load messages:', messagingApi.errorMessage(e));
      } finally {
        this.loading = false;
      }
    },

    toggleExpand(key) {
      const opening = this.expandedKey !== key;
      this.expandedKey = opening ? key : null;
      if (opening) {
        const thread = this.threads.find((t) => t.normalizedKey === key);
        if (thread) this.loadThreadDetails(thread);
      }
    },

    async loadThreadDetails(thread) {
      const toFetch = thread.messages.filter((msg) => msg.external_id && !this.messageDetails[msg.id]);
      for (const msg of toFetch) {
        this.messageDetails[msg.id] = { loading: true, detail: null };
      }
      await Promise.all(
        toFetch.map(async (msg) => {
          try {
            const detail = await messagingApi.getMessage(msg.external_id);
            this.messageDetails[msg.id] = { loading: false, detail };
          } catch (e) {
            console.error('[Provider_Messages] failed to load message detail:', messagingApi.errorMessage(e));
            this.messageDetails[msg.id] = { loading: false, detail: null };
          }
        })
      );
    },

    messageBodyText(msg) {
      const detail = this.messageDetails[msg.id]?.detail;
      if (!detail) return '';
      if (detail.text_message && detail.text_message !== 'null') return detail.text_message;
      return '';
    },

    participantLabel(msg, direction) {
      const address = direction === 'from' ? msg.from_address : (msg.to_addresses || [])[0];
      const detail = this.messageDetails[msg.id]?.detail;
      const user = direction === 'from' ? detail?.from_user : detail?.to_user;
      const name = (user && participantDisplayName(user)) || addressBookDisplayName(this.addressBook, address);
      if (name) return name;
      return direction === 'from' ? msg.from_address : (msg.to_addresses || []).join(', ') || '—';
    },

    openNewCompose() {
      this.composeMode = 'new';
      this.composeSubject = '';
      this.composeToEmail = '';
      this.composeToDisplayName = '';
      this.showCompose = true;
    },

    // Reply goes to whoever the current user was corresponding with on the
    // most recent message in the thread: the sender if it was inbound, or
    // the original recipient if it was outbound.
    threadCounterpart(thread) {
      const msg = thread.messages[thread.messages.length - 1];
      if (!msg) return { email: '', name: '' };
      const address = msg.direction === 'inbound' ? msg.from_address : (msg.to_addresses || [])[0];
      const detail = this.messageDetails[msg.id]?.detail;
      const user = msg.direction === 'inbound' ? detail?.from_user : detail?.to_user;
      const name = (user && participantDisplayName(user)) || addressBookDisplayName(this.addressBook, address);
      return { email: address || '', name: name || '' };
    },

    openReplyCompose(thread) {
      this.composeMode = 'reply';
      const base = thread.subject || '';
      this.composeSubject = base.startsWith('Re: ') ? base : `Re: ${base}`;
      const { email, name } = this.threadCounterpart(thread);
      this.composeToEmail = email;
      this.composeToDisplayName = name;
      this.showCompose = true;
    },

    async onComposeSent() {
      await this.loadMessages();
    },

    normalizeSubject,

    formatDate(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },

    formatDateTime(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    },
  },
};
</script>
